/**
 * Environment configuration for the application
 *
 * This module centralizes all environment-specific configuration
 * and provides utilities for determining which services to use
 * based on the current environment.
 */
import { logger } from './logger';

// Firebase project ID
export const PROJECT_ID = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'bodysharing-4b51e';

// Base URLs for different environments
export const LOCALHOST_URL = `http://localhost:5001/${PROJECT_ID}/us-central1`;
export const PRODUCTION_URL = `https://us-central1-${PROJECT_ID}.cloudfunctions.net`;

// Frontend URLs
export const LOCALHOST_FRONTEND = 'http://localhost:3000';
export const PRODUCTION_FRONTEND = `https://${PROJECT_ID}.web.app`;
export const CUSTOM_DOMAIN = 'https://body-sharing.com';

/**
 * Determines if the application should use emulators
 * @returns boolean indicating if emulators should be used
 */
export const shouldUseEmulators = (): boolean => {
  // In production builds, never use emulators
  if (process.env.NODE_ENV === 'production') {
    return false;
  }

  // In development, use emulators if explicitly enabled
  return process.env.REACT_APP_RUN_IN_EMULATOR === 'true';
};

/**
 * Returns the appropriate API base URL based on environment
 */
export const getApiBaseUrl = (): string => {
  return shouldUseEmulators() ? LOCALHOST_URL : PRODUCTION_URL;
};

/**
 * Returns the appropriate frontend URL based on environment
 */
export const getFrontendUrl = (): string => {
  return shouldUseEmulators() ? LOCALHOST_FRONTEND : PRODUCTION_FRONTEND;
};

/**
 * Get all explicitly allowed origins as an array
 * @return {string[]} Array of allowed origins as strings
 */
export const getAllowedOrigins = (): string[] => {
  return [
    LOCALHOST_FRONTEND,
    PRODUCTION_FRONTEND,
    CUSTOM_DOMAIN,
    // Add any other specific domains here
  ];
};

/**
 * Log current environment configuration
 */
export const logEnvironmentConfig = (): void => {
  logger.info('🌐 Environment Configuration', {
    environment: process.env.NODE_ENV,
    usingEmulators: shouldUseEmulators(),
    apiUrl: getApiBaseUrl(),
    frontendUrl: getFrontendUrl(),
  });
};